/**
 * Billing context: loads billing enforcement state, plan, usage, and meeting-start permission.
 * All billing-aware components read from here.
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { stripeAPI } from '../services/api'
import { useAuth } from './AuthContext'

const BillingContext = createContext(null)

export function BillingProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const [billingStatus, setBillingStatus] = useState({
    enforced: false,
    plan: null,
    usage: null,
    canStartMeeting: true,
    loading: true,
  })

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setBillingStatus({ enforced: false, plan: null, usage: null, canStartMeeting: true, loading: false })
      return
    }
    try {
      const res = await stripeAPI.getBillingStatus()
      const data = res?.data ?? res
      setBillingStatus({
        enforced: data?.enforced ?? false,
        plan: data?.plan ?? null,
        usage: data?.usage ?? null,
        canStartMeeting: data?.canStartMeeting ?? true,
        loading: false,
      })
    } catch {
      setBillingStatus((prev) => ({ ...prev, loading: false }))
    }
  }, [isAuthenticated])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value = {
    ...billingStatus,
    refresh,
  }

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  )
}

export function useBilling() {
  const ctx = useContext(BillingContext)
  if (!ctx) throw new Error('useBilling must be used within BillingProvider')
  return ctx
}
